import { InputField } from '@/components/form/InputField';
import PriceDisplay from '@/components/PriceDisplay';
import { Form } from '@/components/primitives/Form';
import ToggleableRow from '@/components/purchase/checkout/ToggleableRow';
import { toastSuccess } from '@/components/toast';
import { getProfileActiveVouchers } from '@/features/profile/profileActions';
import {
  activeVouchersProfileSelector,
  profileDataSelector,
} from '@/features/profile/profileSelectors';
import { getPurchaseFlowBookingById } from '@/features/purchase/purchaseActions';
import { purchaseFlowBookingSelector } from '@/features/purchase/purchaseSelectors';
import {
  addVoucherToBooking,
  removeVoucherFromBooking,
  validateVoucher,
} from '@/features/purchase/purchaseService';
import useConfiguredCurrency from '@/hooks/useConfiguredCurrency';
import { useTranslation } from '@/i18n';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import type {
  MarkVoucherForPaymentPayloadDTO,
  PendingVoucherDTO,
} from '@/types/dto/agent';
import { format } from '@/utils/dateTime';
import { showErrorToast } from '@/utils/errorToast';
import { createVouchersSchema, type VouchersValues } from '@/utils/zodSchema';
import {
  Button,
  Divider,
  Icons,
  Radio,
  RadioGroup,
  Typography,
} from '@fleet/webshop-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

function Vouchers() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { currency } = useConfiguredCurrency();
  const booking = useSelector(purchaseFlowBookingSelector);
  const profile = useSelector(profileDataSelector);
  const activeVouchers = useSelector(activeVouchersProfileSelector);

  const form = useForm<VouchersValues>({
    resolver: zodResolver(createVouchersSchema(t)),
    defaultValues: {
      voucherCode: '',
      _meta: {
        customCode: '',
        selectedVoucherId: null,
      },
    },
  });

  // Fetch active vouchers if user is logged in and vouchers haven't been fetched
  useEffect(() => {
    if (profile && activeVouchers === null) {
      dispatch(getProfileActiveVouchers());
    }
  }, [profile, activeVouchers, dispatch]);

  // Watch form values
  const selectedVoucherId = form.watch('_meta.selectedVoucherId');
  const customCode = form.watch('_meta.customCode');

  // Update voucherCode when customCode changes and custom row is selected
  useEffect(() => {
    if (selectedVoucherId === -1 && customCode) {
      form.setValue('voucherCode', customCode);
    }
  }, [customCode, selectedVoucherId, form]);

  // Handle selecting a pre-defined voucher
  const handleVoucherSelect = useCallback(
    (voucherId: number, voucherCode: string) => {
      form.setValue('voucherCode', voucherCode);
      form.setValue('_meta.selectedVoucherId', voucherId);
      form.clearErrors('voucherCode');
    },
    [form]
  );

  // Handle selecting the custom code row
  const handleCustomCodeSelect = useCallback(() => {
    form.setValue('_meta.selectedVoucherId', -1);
    const currentCustomCode = form.getValues('_meta.customCode');
    form.setValue('voucherCode', currentCustomCode || '');
    form.clearErrors('voucherCode');
  }, [form]);

  // Render the content for the custom voucher code row
  function renderCustomCodeContent() {
    if (selectedVoucherId === -1) {
      return (
        <InputField
          name="_meta.customCode"
          placeholder={`${t('text.exempliGratia')} 2025ACDF10`}
          hideLabel
          label=""
          classNames={{ wrapper: 'mb-0 flex-1' }}
        />
      );
    }

    if (customCode) {
      return (
        <div className="flex items-center gap-1">
          <Typography variant="body2-bold">{customCode}</Typography>
          <Typography variant="body2" className="text-neutral">
            (<TransText i18nKey="custom" />)
          </Typography>
        </div>
      );
    }

    return (
      <Typography variant="body2">
        <TransText i18nKey="addNewVoucherCode" />
      </Typography>
    );
  }

  const onSubmit = useCallback(
    async (values: VouchersValues) => {
      setLoading(true);
      const { voucherCode } = values;
      if (!booking?.id) return;

      try {
        const { voucherRemainingValue } = await validateVoucher(
          voucherCode,
          booking.id
        );

        const paidAmount: MarkVoucherForPaymentPayloadDTO['paidAmount'] = {
          amount: Math.min(
            voucherRemainingValue,
            booking?.provisionalPrice?.amount || 0
          ),
          currency,
        };

        await addVoucherToBooking(voucherCode, booking.id, paidAmount);
        toastSuccess(<TransAlert i18nKey="voucherApplied" />);
      } catch (e) {
        showErrorToast(
          <TransText i18nKey="voucherCodeNotValid" />,
          e as ApiErrorResponse
        );
        return;
      } finally {
        setLoading(false);
      }
      await dispatch(getPurchaseFlowBookingById(booking.id)).unwrap();
    },
    [booking?.id, booking?.provisionalPrice?.amount, currency, dispatch, t]
  );

  const handleRemoveVoucher = useCallback(
    async (voucherCode: string) => {
      setLoading(true);
      if (!booking?.id) return;

      await removeVoucherFromBooking(voucherCode, booking.id);
      setLoading(false);

      await dispatch(getPurchaseFlowBookingById(booking.id)).unwrap();
    },
    [booking?.id, dispatch]
  );

  const renderAppliedVoucher = useCallback(
    (voucher: PendingVoucherDTO, index: number) => (
      <div
        className="align-center flex w-full justify-between rounded-md border border-success-border bg-success-surface py-2 pl-2 pr-3"
        key={index}
      >
        <div className="flex shrink-0 items-center gap-2">
          <Icons.checkCircle className="h-4 w-4 text-success-text" />
          <Typography variant="body2-bold">
            <PriceDisplay
              amount={voucher.amount.amount / 10 ** (voucher.amount.scale || 0)}
            />
          </Typography>
        </div>
        <div className="flex items-center gap-2">
          <Typography className="text-neutral" variant="body2">
            {voucher.voucher_code}
          </Typography>
          <Button
            variant="icon"
            data-testid="remove-applied-voucher-button"
            onClick={() => handleRemoveVoucher(voucher.voucher_code)}
            className="h-6 w-6 text-error-text"
          >
            <Icons.x className="h-6 w-6" />
          </Button>
        </div>
      </div>
    ),
    [handleRemoveVoucher]
  );

  return (
    <div className="flex h-full w-full flex-col gap-3 laptop:flex-row laptop:gap-6">
      <div className="flex-1 pt-2">
        <Form
          form={form}
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-5"
        >
          <div className="flex flex-col gap-2">
            <Typography variant="body1-bold" className="mb-2 text-dark">
              <TransText i18nKey="addVoucher" />:
            </Typography>

            {profile &&
              activeVouchers?.items &&
              activeVouchers.items.length > 0 && (
                <RadioGroup
                  value={
                    selectedVoucherId != null ? String(selectedVoucherId) : ''
                  }
                  isReadOnly
                  className="mb-2"
                >
                  {activeVouchers.items
                    .filter(
                      (voucher) => voucher.id != null && voucher.code != null
                    )
                    .map((voucher) => (
                      <ToggleableRow
                        key={voucher.id!}
                        isActive={selectedVoucherId === voucher.id}
                        onClick={() =>
                          handleVoucherSelect(voucher.id!, voucher.code!)
                        }
                        data-testid={`voucher-row-${voucher.id}`}
                        className="flex-row"
                      >
                        <Radio value={String(voucher.id!)} size="md" />
                        <div className="flex flex-1 items-center justify-between">
                          <div className="flex flex-col">
                            <Typography variant="body2-bold">
                              {voucher.code}
                            </Typography>
                            {voucher.validUntil && (
                              <Typography
                                variant="body2"
                                className="text-neutral"
                              >
                                {format(voucher.validUntil, 'EEE, dd MMM yyyy')}
                              </Typography>
                            )}
                          </div>
                          <div className="flex flex-row items-center gap-2">
                            <Typography
                              variant="body2"
                              className="text-neutral"
                            >
                              <TransText i18nKey="remaining" />:
                            </Typography>
                            <Typography variant="body2-bold">
                              <PriceDisplay amount={voucher.balance} />
                            </Typography>
                          </div>
                        </div>
                      </ToggleableRow>
                    ))}

                  {/* Custom code row */}
                  <ToggleableRow
                    isActive={selectedVoucherId === -1}
                    onClick={handleCustomCodeSelect}
                    data-testid="custom-voucher-row"
                    className="flex-row"
                  >
                    <Radio value="-1" size="md" />
                    <div className="flex flex-1">
                      {renderCustomCodeContent()}
                    </div>
                  </ToggleableRow>
                </RadioGroup>
              )}

            {/* Fallback input field - shown when not logged in or no vouchers */}
            {(!profile ||
              !activeVouchers?.items ||
              activeVouchers.items.length === 0) && (
              <InputField
                name="voucherCode"
                placeholder={`${t('text.exempliGratia')} 2025ACDF10`}
                required={true}
                hideLabel
                label=""
              />
            )}
          </div>

          <Button
            variant="secondary"
            className="flex w-full gap-1.5 laptop:w-fit"
            data-testid="add-voucher-button"
            isPending={loading}
            type="submit"
            startAdornment={
              <Icons.check className="h-5 w-5 laptop:h-4 laptop:w-4" />
            }
          >
            <TransText i18nKey="apply" />
          </Button>
        </Form>
      </div>
      <Divider
        orientation="vertical"
        grow={false}
        className="my-2 h-auto self-stretch laptop:my-0"
      />
      <div className="flex w-full flex-1 flex-col gap-2 py-2">
        <Typography variant="body1-bold" className="pb-1">
          <TransText i18nKey="appliedVouchers" />:
        </Typography>
        <div className="flex w-full flex-col gap-2">
          {booking?.pendingVouchers?.length ? (
            booking.pendingVouchers.map(renderAppliedVoucher)
          ) : (
            <Typography variant="body2" className="text-neutral">
              <TransText i18nKey="noVouchersApplied" />
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
}

export default Vouchers;
